import { useEffect, useState } from 'react';
import type { Sesion } from '../lib/api';
import { useSesion } from '../lib/sesion';
import { horaVE } from '../lib/fechas';
import {
  FRASES, despedidaDeHora, elegirFrase, grupoDeRoles, nombreSaludo, saludoDeHora,
  type GrupoFrase,
} from '../lib/frases-motivacionales';
import logoGrupoDH from '../imagenes/flash/logo-grupodh.png';
import petalosGrupoDH from '../imagenes/frase/petalos-grupodh.png';

/**
 * LA FRASE MOTIVACIONAL AL ENTRAR (2026-10-26). El guion —grupos, roles y
 * frases— vive en `lib/frases-motivacionales.ts`; esto es solo la ventana.
 *
 * Sale una vez por inicio de sesión (la marca la pone `entrar()`), se cierra con
 * la ✕ que pidió la usuaria, con «Continuar al portal» o con Escape, y no deja
 * rastro en el servidor: no hay nada que guardar. Lo único que se recuerda, en
 * ESTE navegador, son las frases que la persona ya vio, para no repetírselas
 * hasta haberlas visto todas. Si el navegador no deja guardar (modo privado), la
 * frase sale igual, elegida al azar.
 *
 * El GRUPO no se nombra en pantalla (pedido de la usuaria el mismo día): elige
 * qué frases le tocan a cada quien y nada más. La primera versión pintaba
 * «Frase de liderazgo» encima del saludo.
 *
 * El diseño (segunda versión, también del 26/10, «más llamativo y con el logo de
 * Grupo DH»): cabecera clara con el logotipo completo, la frase en grande sobre
 * el degradado de marca —magenta a navy, los dos colores del logo— con los
 * pétalos del logo de marca de agua, y un brillo que cruza la banda al abrir.
 * Las animaciones viven en `estilos.css` (`.frase-*`) y se apagan con «reducir
 * movimiento».
 *
 * Con la clave maestra también sale, con la frase del rol de la cuenta abierta:
 * es lo que vería esa persona, que es para lo que se entra así. Las vistas se
 * guardan aparte (`:como`) para no gastarle a la persona las suyas.
 */
export default function FraseMotivacional() {
  const { sesion, fraseDeEntrada, cerrarFraseDeEntrada } = useSesion();
  if (!fraseDeEntrada || !sesion) return null;
  /* La ventana se MONTA al abrirse y se desmonta al cerrarse: así la frase se
   * elige en el inicializador de su estado, una sola vez por apertura, y no en
   * un efecto que `<StrictMode>` correría dos veces gastando dos frases. */
  return <Ventana sesion={sesion} onCerrar={cerrarFraseDeEntrada} />;
}

function claveVistas(username: string, grupo: GrupoFrase, suplantando: boolean) {
  return `pm-frases:${username}:${grupo}${suplantando ? ':como' : ''}`;
}

function leerVistas(clave: string): number[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(clave) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [];
  } catch {
    return [];
  }
}

function guardarVistas(clave: string, vistas: number[]) {
  try {
    localStorage.setItem(clave, JSON.stringify(vistas));
  } catch {
    /* Sin almacenamiento la frase sale igual; solo se pierde el «no repetir». */
  }
}

/**
 * LOS COLORES DE LA BANDA (2026-10-26, la usuaria: «usa otros colores, todo es
 * blanco y lila; usa otros colores de la paleta»). Cinco degradados de la paleta
 * del portal (`.tema-*` en `estilos.css`, con su contraste medido), uno por
 * entrada y sin repetir el de la vez anterior. El acento del saludo va a juego,
 * con el token de TINTA del mismo color, que en tema oscuro se aclara solo.
 */
const TEMAS = [
  { clase: 'tema-magenta', tinta: 'text-magenta' },
  { clase: 'tema-bosque', tinta: 'text-marca-hd' },
  { clase: 'tema-oceano', tinta: 'text-empresa-ddh' },
  { clase: 'tema-fuego', tinta: 'text-marca-gama' },
  { clase: 'tema-ambar', tinta: 'text-empresa-dhv' },
] as const;
const CLAVE_TEMA = 'pm-frase-tema';

/** Un color al azar que no sea el de la entrada anterior. Solo LEE. */
function elegirTema(): number {
  let anterior = -1;
  try { anterior = Number(localStorage.getItem(CLAVE_TEMA) ?? -1); } catch { /* sin almacenamiento */ }
  const otros = TEMAS.map((_, i) => i).filter((i) => i !== anterior);
  return otros[Math.floor(Math.random() * otros.length)] ?? 0;
}

/** Un destello de cuatro puntas, el adorno de la banda. */
function Destello({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={'frase-destello pointer-events-none absolute '
      + className}>
      <path fill="currentColor" d="M12 0c.6 6.2 5.8 11.4 12 12-6.2.6-11.4 5.8-12 12-.6-6.2-5.8-11.4-12-12C6.2 11.4 11.4 6.2 12 0z" />
    </svg>
  );
}

function Ventana({ sesion, onCerrar }: { sesion: Sesion; onCerrar: () => void }) {
  const grupo = grupoDeRoles(sesion.roles);
  const clave = claveVistas(sesion.usuario.username, grupo, !!sesion.suplantacion);

  /* Leer y sortear no escribe nada, así que el doble inicializador de
   * `<StrictMode>` no gasta frases; escribir va en el efecto, que repetido
   * guarda lo mismo. */
  const [eleccion] = useState(() => elegirFrase(grupo, leerVistas(clave)));
  /* La hora de CARACAS, no la del equipo (`lib/fechas.ts`, punto 2). Manda el
   * saludo de arriba y la despedida del pie. */
  const [hora] = useState(() => Number(horaVE(new Date().toISOString()).slice(0, 2)));

  const [tema] = useState(elegirTema);
  const { clase, tinta } = TEMAS[tema]!;

  useEffect(() => { guardarVistas(clave, eleccion.vistas); }, [clave, eleccion]);
  useEffect(() => {
    try { localStorage.setItem(CLAVE_TEMA, String(tema)); } catch { /* sin almacenamiento */ }
  }, [tema]);

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onCerrar]);

  return (
    /* Por encima del recorrido (z-60…62) por si coincidieran. No coinciden: el
     * saludo del recorrido espera a que esta ventana se cierre (`Recorrido.tsx`).
     * El desplazamiento va en la cortina y el centrado en un envoltorio con
     * `min-h-full`: centrar con flex en la propia cortina cortaría la cabecera en
     * una pantalla más baja que la tarjeta (un teléfono apaisado). */
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/60 backdrop-blur-sm">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="frase-saludo"
          aria-describedby="frase-texto"
          className="frase-entrada relative w-full max-w-xl overflow-hidden rounded-3xl bg-panel shadow-2xl"
        >
          {/* La franja con los colores de las marcas y las empresas. */}
          <div aria-hidden="true" className="franja-paleta" />

          {/* ── El logotipo y el saludo ─────────────────────────────────────── */}
          <div className="relative px-6 pb-5 pt-6 text-center sm:px-10">
            <button
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar la frase y continuar al portal"
              title="Cerrar"
              className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-suave
                         text-base leading-none text-texto-2 transition hover:text-texto
                         hover:ring-2 hover:ring-magenta"
            >
              ✕
            </button>
            {/* En tema oscuro, la «versión negativa» del logo, igual que en la
              * cabecera del portal (`Marco.tsx`). */}
            <img
              src={logoGrupoDH}
              alt="Grupo DH — Inspirados por ti"
              className="mx-auto h-14 w-auto max-w-[78%] object-contain sm:h-16
                         dark:invert dark:hue-rotate-180"
            />
            <p className={`mt-4 text-[11px] font-bold uppercase tracking-[0.24em] ${tinta}`}>
              {saludoDeHora(hora)}
            </p>
            <h2 id="frase-saludo" className="mt-0.5 text-xl font-black leading-tight text-texto sm:text-2xl">
              {nombreSaludo(sesion.usuario.nombre)}
            </h2>
          </div>

          {/* ── La frase, sobre el degradado del color que tocó ─────────────── */}
          <div className={`frase-banda relative overflow-hidden ${clase} px-7 pb-9 pt-7 text-white
                           sm:px-11 sm:pb-11`}>
            <img
              src={petalosGrupoDH}
              alt=""
              aria-hidden="true"
              className="frase-petalos pointer-events-none absolute -bottom-12 -right-6 h-60 w-auto
                         opacity-20 brightness-0 invert sm:h-72"
            />
            <Destello className="right-[18%] top-5 h-5 w-5 text-white/90" />
            <Destello className="right-[9%] top-14 h-3 w-3 text-white/70 [animation-delay:0.6s]" />
            <Destello className="bottom-6 left-[46%] h-3.5 w-3.5 text-white/60 [animation-delay:1.2s]" />

            <span aria-hidden="true" className="block font-serif text-[5.5rem] leading-[0.7] text-white/45">
              “
            </span>
            <p id="frase-texto" className="relative mt-1 text-2xl font-black leading-snug sm:text-[1.75rem]">
              {FRASES[grupo][eleccion.indice]}
            </p>
            <div aria-hidden="true" className="relative mt-6 flex items-center gap-2">
              <span className="h-1 w-14 rounded-full bg-white" />
              <span className="h-1 w-3 rounded-full bg-white/60" />
              <span className="h-1 w-1.5 rounded-full bg-white/40" />
            </div>
          </div>

          {/* ── La salida ───────────────────────────────────────────────────── */}
          <div className="flex flex-col-reverse items-stretch gap-3 px-6 py-5 sm:flex-row sm:items-center
                          sm:justify-between sm:px-10">
            <span className="flex items-center justify-center gap-2 text-sm text-texto-2">
              <img src={petalosGrupoDH} alt="" aria-hidden="true"
                className="h-5 w-auto dark:invert dark:hue-rotate-180" />
              {despedidaDeHora(hora)}
            </span>
            <button
              type="button"
              autoFocus
              onClick={onCerrar}
              className={`whitespace-nowrap rounded-full ${clase} px-6 py-3 text-sm font-bold text-white
                          shadow-lg transition hover:brightness-110`}
            >
              Continuar al portal ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
